import type { Scope } from '@sentry/browser'
import type { LoggerAdapter, SentryAdapterContext } from '../types'

type Extras = Record<string, unknown> | undefined

const setExtras = (scope: Scope, extras?: Extras) => {
  if (extras) {
    scope.setExtras(extras)
  }
}

const updateContext =
  (scope: Scope) =>
  ({ userId, tags }: SentryAdapterContext) => {
    if (userId !== undefined) {
      scope.setUser({ id: userId })
    }
    if (tags) {
      scope.setTags(tags)
    }
  }

const EXCLUDED_PROPERTIES = [
  'toJSON',
  ...Object.getOwnPropertyNames(Object.getPrototypeOf({})),
]

const IGNORED_ERRORS = [
  'Network Error',
  'Unable to fetch result (Network Error)',
  /^Failed to fetch \(.+\)$/, // https://wix-o.sentry.io/issues/6941193207/
  'Failed to fetch', // https://wix-o.sentry.io/issues/7098500617/
  /^NetworkError when attempting to fetch resource. \(.+\)$/, // https://wix-o.sentry.io/issues/7119035454/
  /^Load failed \(.+\)$/, // https://wix-o.sentry.io/issues/7119239206/
  'Request aborted',
  'Unable to fetch result (Request aborted)',
  'Request failed with status code 401',
  'Unable to fetch result (Request failed with status code 401)',
  'Request failed with status code 403',
  'Unable to fetch result (Request failed with status code 403)',
  'Request failed with status code 499',
  'Unable to fetch result (Request failed with status code 499)',
  'Request failed with status code 500',
  'Unable to fetch result (Request failed with status code 500)',
  'Request failed with status code 501',
  'Unable to fetch result (Request failed with status code 501)',
  'Request failed with status code 503',
  'Unable to fetch result (Request failed with status code 503)',
  'Request failed with status code 504',
  'Unable to fetch result (Request failed with status code 504)',
  'Failed to sync routers', // ignoring until the backend issue is resolved by Rimvydas Gimbutas
  'Failed to list all controllers', // ignoring until https://wix.atlassian.net/browse/DM-14002 is resolved
  'Wix Code is installed, but Wix Code public API is not available', // ignoring until this issue is resolved https://wix.slack.com/archives/C025ST7GB3N/p1766145215148469
  'Unexpected token \'x\', "x"xxxxxxxx"... is not valid JSON', // ignoring until this issue is resolved https://wix.slack.com/archives/CQQ6MNTV5/p1766318763504529
]

const shouldIgnoreError = (errorCauseChain: any[]) => {
  return IGNORED_ERRORS.some(matcher =>
    errorCauseChain.some(error => {
      if (typeof error !== 'object' || error === null) {
        return false
      }

      const { message } = error
      if (typeof matcher === 'string') {
        return matcher === message
      } else if (matcher instanceof RegExp) {
        return !!message && matcher.test(message)
      } else {
        return false
      }
    }),
  )
}

const flattenObject = (object: object) => {
  const flat: Record<string, any> = {}
  let proto = object
  while (proto) {
    for (const i of Object.getOwnPropertyNames(proto)) {
      let value
      try {
        value = (object as any)[i]
      } catch {
        // in case a getter throws
        continue
      }

      if (!EXCLUDED_PROPERTIES.includes(i) && typeof value !== 'function') {
        flat[i] = value
      }
    }

    proto = Object.getPrototypeOf(proto)
  }
  return flat
}

const recursiveFlatten = (
  value: unknown,
  visitedObjects: Set<unknown> = new Set(),
): any => {
  if (typeof value !== 'object' || value === null) {
    return value
  }

  if (visitedObjects.has(value)) {
    return '[visited]'
  }
  visitedObjects.add(value)

  const object = flattenObject(value)
  Object.keys(object).forEach(key => {
    object[key] = recursiveFlatten(object[key], visitedObjects)
  })
  return object
}

const gatherErrorCauseChain = (error: any) => {
  const errors = []
  let e = error
  const visitedCauses = new Set()
  while (true) {
    visitedCauses.add(e)

    const object = recursiveFlatten(e)
    if (typeof object === 'object' && object !== null) {
      delete object.cause
    }
    errors.push(object)

    const hasCause = typeof e === 'object' && e !== null && 'cause' in e
    if (hasCause && !visitedCauses.has(e.cause)) {
      e = e.cause
    } else {
      break
    }
  }
  errors.reverse()
  return errors
}

export type SentryAdapter = Required<
  Pick<LoggerAdapter, 'error' | 'info' | 'breadcrumb' | 'updateContext'>
>

export const sentryAdapter = (client: Scope): SentryAdapter => {
  const adapter: SentryAdapter = {
    error: (error, extras) => {
      try {
        const errorCauseChain = gatherErrorCauseChain(error)

        if (shouldIgnoreError(errorCauseChain)) {
          client.addBreadcrumb({
            message: 'Ignored an error',
            category: 'sentryAdapter',
            data: { errorCauseChain },
          })
          return
        }

        const scope = client.clone()
        scope.setTag('priority', 'P3')
        setExtras(scope, {
          ...extras,
          errorCauseChain,
        })
        scope.captureException(error)
      } catch (loggerError) {
        client.captureException(
          new Error('sentryAdapter.error failed', { cause: loggerError }),
        )
      }
    },
    info: (message, extras) => {
      const scope = client.clone()
      setExtras(scope, extras)
      client.captureMessage(message, 'info')
    },
    breadcrumb: ({ message, category, data }) => {
      client.addBreadcrumb({
        message,
        category,
        data,
      })
    },
    updateContext: context => {
      if ('userId' in context || 'tags' in context) {
        updateContext(client)(context)
      }
    },
  }

  return adapter
}
